import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ShieldCheck, KeyRound } from 'lucide-react';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Alert } from '../components/ui/alert';
import { useAuth } from '../contexts/AuthContext';
import {
  completeBootstrapActivation,
  getBootstrapStatus,
  sendBootstrapActivationCode,
} from '../services/auth.service';

const activationSchema = z
  .object({
    name: z.string().min(2, 'Name is required'),
    tenantName: z.string().optional(),
    email: z.string().email('Invalid email'),
    password: z
      .string()
      .min(8, 'min8')
      .regex(/[A-Z]/, 'requirements')
      .regex(/[a-z]/, 'requirements')
      .regex(/[0-9]/, 'requirements')
      .regex(/[!@#$%^&*(),.?":{}|<>]/, 'requirements'),
    confirmPassword: z.string().min(1, 'required'),
    verificationCode: z.string().length(6, 'Verification code must be 6 digits'),
  })
  .refine((data) => data.password === data.confirmPassword, {
    path: ['confirmPassword'],
    message: 'mismatch',
  });

type ActivationFormValues = z.infer<typeof activationSchema>;

const ActivateService: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { applyAuthPayload, isAuthenticated } = useAuth();

  const [error, setError] = useState('');
  const [isChecking, setIsChecking] = useState(true);
  const [isSendingCode, setIsSendingCode] = useState(false);
  const [isActivating, setIsActivating] = useState(false);
  const [codeSent, setCodeSent] = useState(false);

  const {
    register,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<ActivationFormValues>({
    resolver: zodResolver(activationSchema),
    defaultValues: {
      name: '',
      tenantName: '',
      email: '',
      password: '',
      confirmPassword: '',
      verificationCode: '',
    },
  });

  useEffect(() => {
    const check = async () => {
      try {
        if (isAuthenticated) {
          navigate('/', { replace: true });
          return;
        }
        const status = await getBootstrapStatus();
        if (!status.requiresActivation) {
          navigate('/login', { replace: true });
          return;
        }
      } catch {
        setError('Unable to verify activation status');
      } finally {
        setIsChecking(false);
      }
    };

    check();
  }, [isAuthenticated, navigate]);

  const handleSendCode = async () => {
    setError('');

    const email = getValues('email');
    if (!email) {
      setError('Email is required');
      return;
    }

    try {
      setIsSendingCode(true);
      await sendBootstrapActivationCode(email);
      setCodeSent(true);
    } catch {
      setError('Unable to send verification code');
    } finally {
      setIsSendingCode(false);
    }
  };

  const onSubmit = async (values: ActivationFormValues) => {
    setError('');
    try {
      setIsActivating(true);
      const payload = await completeBootstrapActivation({
        name: values.name,
        tenantName: values.tenantName || undefined,
        email: values.email,
        password: values.password,
        verificationCode: values.verificationCode,
      });
      applyAuthPayload(payload);
      navigate('/', { replace: true });
    } catch {
      setError('Activation failed. Please verify code and data.');
    } finally {
      setIsActivating(false);
    }
  };

  if (isChecking) {
    return null;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 p-4 dark:from-slate-950 dark:to-slate-900">
      <div className="mx-auto flex min-h-screen max-w-xl items-center justify-center">
        <Card className="w-full shadow-lg">
          <CardHeader className="space-y-2 text-center">
            <div className="mx-auto mb-2 flex h-12 w-12 items-center justify-center rounded-full bg-orange-100 dark:bg-orange-900">
              <ShieldCheck className="h-6 w-6 text-orange-600 dark:text-orange-400" />
            </div>
            <CardTitle className="text-2xl font-bold">
              {t('activation.title', 'Activate Timesheet Service')}
            </CardTitle>
            <CardDescription>
              {t('activation.subtitle', 'Create the first Super Admin account and verify your email.')}
            </CardDescription>
          </CardHeader>

          <CardContent>
            {error && <Alert variant="error" className="mb-4">{error}</Alert>}

            <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">{t('activation.name', 'Full Name')}</Label>
                <Input id="name" {...register('name')} disabled={isSendingCode || isActivating} />
                {errors.name && <p className="text-sm text-red-500">{errors.name.message}</p>}
              </div>

              <div className="space-y-2">
                <Label htmlFor="tenantName">{t('activation.tenantName', 'Company Name (optional)')}</Label>
                <Input id="tenantName" {...register('tenantName')} disabled={isSendingCode || isActivating} />
              </div>

              <div className="space-y-2">
                <Label htmlFor="email">{t('activation.email', 'Email')}</Label>
                <Input id="email" type="email" {...register('email')} disabled={isSendingCode || isActivating || codeSent} />
                {errors.email && <p className="text-sm text-red-500">{errors.email.message}</p>}
              </div>

              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="password">{t('activation.password', 'Password')}</Label>
                  <Input id="password" type="password" {...register('password')} disabled={isSendingCode || isActivating} />
                  {errors.password && <p className="text-sm text-red-500">{t('pages.resetPassword.errors.requirements')}</p>}
                  <p className="text-xs text-muted-foreground">{t('common.passwordRequirements')}</p>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmPassword">{t('activation.confirmPassword', 'Confirm Password')}</Label>
                  <Input id="confirmPassword" type="password" {...register('confirmPassword')} disabled={isSendingCode || isActivating} />
                  {errors.confirmPassword && (
                    <p className="text-sm text-red-500">
                      {errors.confirmPassword.message === 'mismatch'
                        ? t('pages.resetPassword.errors.mismatch')
                        : t('pages.resetPassword.errors.confirmRequired')}
                    </p>
                  )}
                </div>
              </div>

              {!codeSent ? (
                <Button type="button" onClick={handleSendCode} className="w-full bg-orange-500 hover:bg-orange-600" disabled={isSendingCode || isActivating}>
                  <KeyRound className="mr-2 h-4 w-4" />
                  {isSendingCode
                    ? t('activation.sendingCode', 'Sending code...')
                    : t('activation.sendCode', 'Send verification code')}
                </Button>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="verificationCode">{t('activation.code', 'Verification Code')}</Label>
                    <Input
                      id="verificationCode"
                      inputMode="numeric"
                      maxLength={6}
                      {...register('verificationCode')}
                      disabled={isActivating}
                    />
                    {errors.verificationCode && <p className="text-sm text-red-500">{errors.verificationCode.message}</p>}
                  </div>

                  <div className="flex gap-2">
                    <Button type="button" variant="outline" onClick={handleSendCode} disabled={isSendingCode || isActivating}>
                      {t('activation.resendCode', 'Resend code')}
                    </Button>
                    <Button type="submit" className="flex-1 bg-orange-500 hover:bg-orange-600" disabled={isActivating || isSendingCode}>
                      {isActivating
                        ? t('activation.activating', 'Activating...')
                        : t('activation.activate', 'Activate service')}
                    </Button>
                  </div>
                </>
              )}
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ActivateService;
